import { useMemo, useState, useCallback } from 'react';
import {
  Text, Paper, Stack, Group, Table, Skeleton, Drawer,
  SimpleGrid, Divider, ActionIcon, Tooltip, Pagination,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronUp, IconChevronDown, IconSelector, IconEye, IconReceipt,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { useSubstitutionRules } from '@/hooks/usePortalApi';

type SortField = 'original_product';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

export default function Substitutions() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [drawerItem, setDrawerItem] = useState<any>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const { data, isLoading } = useSubstitutionRules();
  const allItems = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allItems;
    return [...allItems].sort((a, b) => {
      const cmp = String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allItems, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const items = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleView = useCallback((item: any) => { setDrawerItem(item); openDrawer(); }, [openDrawer]);

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader title="Substitutions" description="View product substitution rules" />
      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : items.length === 0 ? (
        <EmptyState icon={<IconReceipt size={30} />} title="No substitution rules" description="No substitution rules found" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>ID</Table.Th>
                  <ThSort field="original_product">Original Product</ThSort>
                  <Table.Th>Substitute Product</Table.Th>
                  <Table.Th>Priority</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item: any) => (
                  <Table.Tr key={item.id}>
                    <Table.Td><Text size="sm" style={{ fontFamily: 'monospace' }}>{item.id}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{item.original_product}</Text></Table.Td>
                    <Table.Td><Text size="sm">{item.substitute_product}</Text></Table.Td>
                    <Table.Td><Text size="sm">{item.priority}</Text></Table.Td>
                    <Table.Td><StatusBadge status={item.status ?? 'active'} /></Table.Td>
                    <Table.Td>
                      <Tooltip label="View"><ActionIcon variant="subtle" size="sm" onClick={() => handleView(item)}><IconEye size={16} /></ActionIcon></Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}
      <Drawer opened={drawerOpen} onClose={closeDrawer} position="right" size="lg" title={<Text fw={700} size="lg">Substitution Details</Text>}>
        {drawerItem && (
          <Stack gap="md">
            <StatusBadge status={drawerItem.status ?? 'active'} />
            <Divider />
            <SimpleGrid cols={2}>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>ID</Text><Text size="sm" fw={500}>{drawerItem.id}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Original Product</Text><Text size="sm" fw={500}>{drawerItem.original_product}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Substitute Product</Text><Text size="sm" fw={500}>{drawerItem.substitute_product}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Priority</Text><Text size="sm" fw={500}>{drawerItem.priority}</Text></div>
            </SimpleGrid>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
